'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import ReactECharts, { type EChartsOption } from 'echarts-for-react';
import { useCallback, useEffect, useMemo } from 'react';
import { getExpensesMetricTooltip } from '@/modules/finances/lib/get-expenses-metric-chart-tooltip';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useFinancesYear } from '../../../hooks/use-finances-year';
import { formatterExpensesMetricChart, getChartAxisLabelByGranularity, getCorrectGranularity, replaceAllNumberLetOneBeforeDot } from '../../../lib/expenses-metric-chart-utils';
import type { CumulativeType } from '../../../lib/expenses-metric-chart-search-params';
import type { ExpensesMetricChartSeriesData, GRANULARITY_OPTIONS, LineChartSeries } from '../../../types';
interface ExpensesMetricChartProps {
  year: string;
  selectedGranularity: GRANULARITY_OPTIONS;
  series: ExpensesMetricChartSeriesData[];
  refExpensesMetricChart: React.RefObject<EChartsOption>;
  isCumulative: boolean;
  cumulativeType: CumulativeType;
}
function ExpensesMetricChart(t0) {
  const $ = _c(117);
  const {
    refExpensesMetricChart,
    series,
    selectedGranularity,
    isCumulative,
    cumulativeType
  } = t0;
  const {
    year
  } = useFinancesYear();
  let t1;
  if ($[0] !== selectedGranularity) {
    t1 = getCorrectGranularity(selectedGranularity);
    $[0] = selectedGranularity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const granularity = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      to: "sm"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isLessMobile = useMediaQuery(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "sm",
      to: "md"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isMobile = useMediaQuery(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      from: "md",
      to: "lg"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isTablet = useMediaQuery(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      from: "lg",
      to: "xl"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const isDesktop1024 = useMediaQuery(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      from: "xl",
      to: "2xl"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isDesktop1280 = useMediaQuery(t6);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      from: "2xl"
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const isDesktop1440 = useMediaQuery(t7);
  const isMobileOrLess = isMobile || isLessMobile;
  const showLineYear = (isMobile || isLessMobile) && (granularity === "monthly" || granularity === "quarterly");
  let t8;
  if ($[8] !== isDesktop1024 || $[9] !== isDesktop1280 || $[10] !== isDesktop1440 || $[11] !== isLessMobile || $[12] !== isMobile || $[13] !== isTablet) {
    t8 = () => {
      if (isLessMobile || isMobile) {
        return 170;
      }
      if (isTablet) {
        return 230;
      }
      if (isDesktop1024) {
        return 225;
      }
      if (isDesktop1280) {
        return 312;
      }
      if (isDesktop1440) {
        return 314;
      }
      return 312;
    };
    $[8] = isDesktop1024;
    $[9] = isDesktop1280;
    $[10] = isDesktop1440;
    $[11] = isLessMobile;
    $[12] = isMobile;
    $[13] = isTablet;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const getHeightGrid = t8;
  let t9;
  if ($[15] !== isDesktop1024 || $[16] !== isDesktop1280 || $[17] !== isDesktop1440 || $[18] !== isLessMobile || $[19] !== isMobile || $[20] !== isTablet) {
    t9 = () => {
      if (isLessMobile || isMobile || isTablet) {
        return 10;
      }
      if (isDesktop1024) {
        return 30;
      }
      if (isDesktop1280 || isDesktop1440) {
        return 11;
      }
      return 11;
    };
    $[15] = isDesktop1024;
    $[16] = isDesktop1280;
    $[17] = isDesktop1440;
    $[18] = isLessMobile;
    $[19] = isMobile;
    $[20] = isTablet;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const getTopGrid = t9;
  let t10;
  if ($[22] !== isDesktop1024 || $[23] !== isDesktop1280 || $[24] !== isDesktop1440 || $[25] !== isMobile || $[26] !== isTablet) {
    t10 = () => {
      if (isMobile) {
        return 4;
      }
      if (isTablet || isDesktop1024) {
        return 0;
      }
      if (isDesktop1280 || isDesktop1440) {
        return 4;
      }
      return 4;
    };
    $[22] = isDesktop1024;
    $[23] = isDesktop1280;
    $[24] = isDesktop1440;
    $[25] = isMobile;
    $[26] = isTablet;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const getRightGrid = t10;
  let t11;
  if ($[28] !== isDesktop1024 || $[29] !== isDesktop1280 || $[30] !== isDesktop1440 || $[31] !== isLessMobile || $[32] !== isMobile || $[33] !== isTablet) {
    t11 = () => {
      if (isLessMobile || isMobile) {
        return 5;
      }
      if (isTablet) {
        return 8;
      }
      if (isDesktop1024) {
        return 24;
      }
      if (isDesktop1280) {
        return 30;
      }
      if (isDesktop1440) {
        return 32;
      }
      return 36;
    };
    $[28] = isDesktop1024;
    $[29] = isDesktop1280;
    $[30] = isDesktop1440;
    $[31] = isLessMobile;
    $[32] = isMobile;
    $[33] = isTablet;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const getMarginYAxis = t11;
  let t12;
  if ($[35] !== isDesktop1024 || $[36] !== isDesktop1280 || $[37] !== isDesktop1440 || $[38] !== isMobile || $[39] !== isTablet) {
    t12 = () => {
      if (isMobile || isTablet) {
        return 12;
      }
      if (isDesktop1024 || isDesktop1280 || isDesktop1440) {
        return 16;
      }
      return 16;
    };
    $[35] = isDesktop1024;
    $[36] = isDesktop1280;
    $[37] = isDesktop1440;
    $[38] = isMobile;
    $[39] = isTablet;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const getMarginXAxis = t12;
  let t13;
  if ($[41] !== isLessMobile || $[42] !== isTablet) {
    t13 = () => {
      if (isTablet) {
        return 12;
      }
      if (isLessMobile) {
        return 8;
      }
      return 9;
    };
    $[41] = isLessMobile;
    $[42] = isTablet;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const getXAxisFontSize = t13;
  let t14;
  if ($[44] !== isLessMobile || $[45] !== isMobile) {
    t14 = () => {
      if (isLessMobile || isMobile) {
        return 12;
      }
      return 14;
    };
    $[44] = isLessMobile;
    $[45] = isMobile;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const getYAxisFontSize = t14;
  const t15 = getXAxisFontSize();
  let t16;
  if ($[47] !== t15) {
    t16 = {
      fontFamily: "var(--font-open-sans-condensed)",
      textAlign: "center" as const,
      color: "#708390",
      fontWeight: 600,
      fontSize: t15,
      verticalAlign: "top" as const,
      interval: 0
    };
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const xAxisStyles = t16;
  const t17 = !isMobile;
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      type: "line",
      lineStyle: {
        color: "#D4D9E1",
        opacity: 0.3,
        width: 1
      }
    };
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== isTablet) {
    t19 = point => {
      const margin = isTablet ? 0 : 8;
      const xPos = point[0] + margin;
      const yPos = point[1] + margin;
      return [xPos, yPos];
    };
    $[50] = isTablet;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== cumulativeType || $[53] !== isCumulative || $[54] !== isDesktop1024 || $[55] !== isMobile || $[56] !== isTablet || $[57] !== selectedGranularity || $[58] !== t17 || $[59] !== t19 || $[60] !== year) {
    t20 = {
      borderRadius: 12,
      show: t17,
      trigger: "axis",
      extraCssText: "z-index: 6",
      backgroundColor: "var(--color-card)",
      axisPointer: t18,
      padding: 0,
      borderColor: "var(--color-card)",
      borderWidth: 1,
      position: t19,
      formatter(params) {
        return getExpensesMetricTooltip({
          params,
          isMobile,
          isTablet,
          isDesktop1024,
          selectedGranularity,
          isCumulative,
          cumulativeType,
          year
        });
      }
    };
    $[52] = cumulativeType;
    $[53] = isCumulative;
    $[54] = isDesktop1024;
    $[55] = isMobile;
    $[56] = isTablet;
    $[57] = selectedGranularity;
    $[58] = t17;
    $[59] = t19;
    $[60] = year;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = getHeightGrid();
  const t22 = getTopGrid();
  const t23 = getRightGrid();
  let t24;
  if ($[62] !== t21 || $[63] !== t22 || $[64] !== t23) {
    t24 = {
      height: t21,
      top: t22,
      right: t23
    };
    $[62] = t21;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== granularity || $[67] !== isMobileOrLess) {
    t25 = getChartAxisLabelByGranularity(granularity, isMobileOrLess, false, true);
    $[66] = granularity;
    $[67] = isMobileOrLess;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      show: false
    };
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  let t28;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      show: false,
      symbolOffset: "left",
      lineStyle: {
        color: "transparent"
      }
    };
    t28 = {
      show: false
    };
    $[70] = t27;
    $[71] = t28;
  } else {
    t27 = $[70];
    t28 = $[71];
  }
  const t29 = getMarginXAxis();
  const t30 = isMobile ? 12 : 14;
  let t31;
  if ($[72] !== granularity || $[73] !== isLessMobile || $[74] !== isMobile || $[75] !== t29 || $[76] !== t30 || $[77] !== xAxisStyles || $[78] !== year) {
    let t32;
    if ($[80] !== xAxisStyles) {
      t32 = {
        month: xAxisStyles,
        year: xAxisStyles
      };
      $[80] = xAxisStyles;
      $[81] = t32;
    } else {
      t32 = $[81];
    }
    t31 = {
      margin: t29,
      color: "var(--color-border)",
      align: "center",
      fontFamily: "var(--font-open-sans-condensed)",
      fontWeight: 700,
      fontSize: t30,
      baseline: "top",
      interval: 0,
      formatter(value) {
        const formatted = formatterExpensesMetricChart(granularity, isMobile, year, value, isLessMobile);
        return formatted;
      },
      rich: t32
    };
    $[72] = granularity;
    $[73] = isLessMobile;
    $[74] = isMobile;
    $[75] = t29;
    $[76] = t30;
    $[77] = xAxisStyles;
    $[78] = year;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[82] !== t25 || $[83] !== t31) {
    t32 = {
      show: true,
      type: "category",
      data: t25,
      splitLine: t26,
      axisLine: t27,
      axisTick: t28,
      axisLabel: t31
    };
    $[82] = t25;
    $[83] = t31;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  const t33 = series.length === 0 ? 0 : null;
  const t34 = series.length === 0 ? 1 : null;
  const t35 = getMarginYAxis();
  let t36;
  if ($[85] !== getYAxisFontSize || $[86] !== t35) {
    t36 = {
      show: true,
      fontFamily: "var(--font-open-sans-condensed)",
      margin: t35,
      formatter(value_0, index) {
        if (value_0 === 0 && index === 0) {
          return value_0.toString();
        }
        return replaceAllNumberLetOneBeforeDot(value_0, true);
      },
      color: "var(--color-foreground)",
      fontSize: getYAxisFontSize(),
      fontWeight: 700
    };
    $[85] = getYAxisFontSize;
    $[86] = t35;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  let t38;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      show: false
    };
    t38 = {
      lineStyle: {
        color: "var(--color-border)",
        width: 0.25
      }
    };
    $[88] = t37;
    $[89] = t38;
  } else {
    t37 = $[88];
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t33 || $[91] !== t34 || $[92] !== t36) {
    t39 = {
      min: t33,
      max: t34,
      show: true,
      axisLabel: t36,
      verticalAlign: "middle",
      type: "value",
      zlevel: -1,
      splitNumber: 9,
      axisLine: t37,
      splitLine: t38
    };
    $[90] = t33;
    $[91] = t34;
    $[92] = t36;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== series || $[95] !== t20 || $[96] !== t24 || $[97] !== t32 || $[98] !== t39) {
    t40 = {
      tooltip: t20,
      grid: t24,
      xAxis: t32,
      yAxis: t39,
      series
    };
    $[94] = series;
    $[95] = t20;
    $[96] = t24;
    $[97] = t32;
    $[98] = t39;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  const options = t40;
  let t41;
  let t42;
  if ($[100] !== options || $[101] !== refExpensesMetricChart) {
    t41 = () => {
      const chartInstance = refExpensesMetricChart.current?.getEchartsInstance();
      chartInstance?.setOption(options, {
        notMerge: true
      });
    };
    t42 = [options, refExpensesMetricChart];
    $[100] = options;
    $[101] = refExpensesMetricChart;
    $[102] = t41;
    $[103] = t42;
  } else {
    t41 = $[102];
    t42 = $[103];
  }
  useEffect(t41, t42);
  let t43;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = cn("relative flex w-full flex-row justify-center", "h-56 w-full", "md:h-67 md:w-full", "lg:h-72 lg:w-full lg:pl-5", "xl:h-89 xl:pl-0", "2xl:-ml-2.5 2xl:h-89");
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  let t45;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      height: "100%",
      width: "100%"
    };
    t45 = {
      renderer: "svg"
    };
    $[105] = t44;
    $[106] = t45;
  } else {
    t44 = $[105];
    t45 = $[106];
  }
  let t46;
  if ($[107] !== options || $[108] !== refExpensesMetricChart) {
    t46 = <ReactECharts ref={refExpensesMetricChart} option={options} style={t44} opts={t45} />;
    $[107] = options;
    $[108] = refExpensesMetricChart;
    $[109] = t46;
  } else {
    t46 = $[109];
  }
  let t47;
  if ($[110] !== isLessMobile || $[111] !== showLineYear || $[112] !== year) {
    t47 = showLineYear && <div data-slot="year-x-axis" className={cn("absolute right-1 bottom-0 left-8 h-2.5", "rounded-b-sm border-r border-b border-l", "border-border", !isLessMobile && "left-10")}><div data-slot="year-text" className={cn("absolute -bottom-1.5 left-1/2 w-14 -translate-x-1/2", "bg-background text-center text-xs font-bold tracking-wide", "text-border", "font-(--font-open-sans-condensed)")}>{year}</div></div>;
    $[110] = isLessMobile;
    $[111] = showLineYear;
    $[112] = year;
    $[113] = t47;
  } else {
    t47 = $[113];
  }
  let t48;
  if ($[114] !== t46 || $[115] !== t47) {
    t48 = <div data-slot="chart-container" className={t43}>{t46}{t47}</div>;
    $[114] = t46;
    $[115] = t47;
    $[116] = t48;
  } else {
    t48 = $[116];
  }
  return t48;
}
export { ExpensesMetricChart };